"use client";

import type { FC } from "react";

import type { BuilderBlock, BuilderContextInterface, RegisteredComponents } from "@builder.io/sdk-react";
import { Blocks, isEditing } from "@builder.io/sdk-react";

import { QueryClient, QueryClientProvider } from "@jti/rest-api";
import { AGE_VERIFICATION_METHOD, HardAgeVerificationPopup } from "@jti/ui/components/HardAgeVerificationPopup";

import { useDisplayModelComponent } from "@/libs/builder/hooks/useDisplayModelComponent";

import type { qrAgeVerificationContainerInputs } from "./QrAgeVerificationContainer.builder";

export type QrAgeVerificationContainerProps = BuilderComponentProps<typeof qrAgeVerificationContainerInputs> & {
  builderBlock: BuilderBlock;
  builderContext: BuilderContextInterface;
  builderComponents: RegisteredComponents;
};

const client = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const AGE_VERIFICATION_METHODS = {
  DIIA_ASYNC: AGE_VERIFICATION_METHOD.DIIA_ASYNC,
  PASS: AGE_VERIFICATION_METHOD.PASS,
} as const;

export const QrAgeVerificationContainer: FC<QrAgeVerificationContainerProps> = ({
  ignoreUrls,
  title,
  logo,
  description,
  continueButtonLabel,
  exitButtonLabel,
  legalNotice,
  alt,
  desktopImageSrc,
  tabletImageSrc,
  mobileImageSrc,
  backButtonLabel,
  verificationTitle,
  mobileQrHelper,
  verificationSteps,
  getNewQrButtonLabel,
  ttlOfCookie,
  logoAlt,
  exitButtonLink,
  qrTtl,
  qrStaleError,
  qrCommonError,
  failedProviderVerificationError,
  ageVerificationMethod,
  waitingMessage,
  helperText,
  signInButtonLabel,
  tryAgainButtonLabel,
  signInURL,
  verificationErrorHelperText,
  missingDataFromAvError,
  backgroundColor,
  backgroundBlur,
  builderBlock,
  builderContext,
  builderComponents,
}) => {
  const shouldDisplayModelComponent = useDisplayModelComponent("qr-age-verification-popup");

  if (!shouldDisplayModelComponent) {
    return null;
  }

  return (
    <QueryClientProvider client={client}>
      <HardAgeVerificationPopup
        externalContent={
          <>
            {builderBlock?.children?.map((child) => (
              <Blocks
                key={child.id}
                blocks={[child]}
                parent={builderBlock?.id}
                path="children"
                context={builderContext}
                registeredComponents={builderComponents}
              />
            ))}
          </>
        }
        ignoreUrls={ignoreUrls.map(({ url }) => url)}
        title={title}
        description={description}
        continueButtonLabel={continueButtonLabel}
        exitButtonLabel={exitButtonLabel}
        legalNotice={legalNotice}
        alt={alt}
        desktopImageSrc={desktopImageSrc}
        tabletImageSrc={tabletImageSrc}
        mobileImageSrc={mobileImageSrc}
        backButtonLabel={backButtonLabel}
        verificationTitle={verificationTitle}
        mobileQrHelper={mobileQrHelper}
        verificationSteps={verificationSteps}
        getNewQrButtonLabel={getNewQrButtonLabel}
        ttlOfCookie={ttlOfCookie}
        logoAlt={logoAlt}
        exitButtonLink={exitButtonLink}
        qrTtl={qrTtl}
        qrStaleError={qrStaleError}
        qrCommonError={qrCommonError}
        failedProviderVerificationError={failedProviderVerificationError}
        waitingMessage={waitingMessage}
        helperText={helperText}
        signInButtonLabel={signInButtonLabel}
        tryAgainButtonLabel={tryAgainButtonLabel}
        signInURL={signInURL}
        verificationErrorHelperText={verificationErrorHelperText}
        missingDataFromAvError={missingDataFromAvError}
        backgroundColor={backgroundColor}
        backgroundBlur={backgroundBlur}
        logoSrc={logo}
        ageVerificationMethod={ageVerificationMethod ? AGE_VERIFICATION_METHODS[ageVerificationMethod] : undefined}
        isOpened={isEditing()}
      />
    </QueryClientProvider>
  );
};
